import { Reveal } from '@/components/Reveal';
import { SEO, createBreadcrumbSchema } from '@/components/SEO';
import { Breadcrumb } from '@/components/Breadcrumb';
import { ContactForm } from '@/components/ContactForm';
import { useSettings } from '@/lib/useSettings';
import { formatWorkingHours } from '@/lib/workingHours';
import { MapPin, Clock, Phone, MessageCircle, Navigation, Mail, Youtube, Instagram, Facebook } from 'lucide-react';

export default function ContactPage() {
  const settings = useSettings();
  const CONTACT_INFO = settings.contact || {};
  const SOCIAL = settings.social || {};
  return (
    <div data-testid="contact-page">
      <SEO title="İletişim & Randevu" description="Prof. Dr. Nilüfer Yıldırım - Randevu ve iletişim bilgileri. Ankara'da fonksiyonel tıp danışmanlığı için bize ulaşın." schema={createBreadcrumbSchema([{ name: 'Ana Sayfa', url: '/' }, { name: 'İletişim' }])} />
      <section className="bg-[#F5F3EF] py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
          <h1 className="font-heading text-[34px] sm:text-[34px] lg:text-[46px] font-semibold tracking-[-0.02em] mb-4">İletişim & Randevu</h1>
          <p className="text-[22px] text-[#5F6B76] max-w-xl">Sorularınız ve randevu talepleriniz için bize ulaşın.</p>
        </div>
      </section>
      <Breadcrumb items={[{ label: 'İletişim', href: '/iletisim', current: true }]} />

      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Left: Form */}
          <Reveal>
            <div className="bg-white rounded-[28px] border border-[#E7E5E4] p-6 sm:p-8 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.08)]">
              <h2 className="font-heading text-[24px] font-semibold mb-2">Bize Yazın</h2>
              <p className="text-[14px] text-[#5F6B76] mb-6">Formu doldurun, en kısa sürede dönüş yapalım.</p>
              <ContactForm />
            </div>
          </Reveal>

          {/* Right: Info */}
          <Reveal delay={100}>
            <div className="space-y-6">
              {/* Quick Actions */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <a
                  href={`tel:${CONTACT_INFO.phone}`}
                  className="flex flex-col items-center gap-2 p-5 bg-white rounded-[20px] border border-[#E7E5E4] hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.12)] hover:-translate-y-0.5 transition-all text-center"
                >
                  <div className="w-10 h-10 rounded-full bg-[#F5F3EF] flex items-center justify-center">
                    <Phone size={18} className="text-[#1F6E7A]" />
                  </div>
                  <span className="text-[14px] font-medium">Ara</span>
                  <span className="text-[14px] text-[#5F6B76]">{CONTACT_INFO.phone}</span>
                </a>
                <a
                  href={`https://wa.me/${CONTACT_INFO.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-5 bg-[#1F6E7A] rounded-[20px] text-white hover:bg-[#185960] hover:-translate-y-0.5 transition-all text-center shadow-[0_8px_24px_-12px_rgba(31,110,122,0.4)]"
                >
                  <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                    <MessageCircle size={18} />
                  </div>
                  <span className="text-[14px] font-medium">WhatsApp</span>
                  <span className="text-[14px] text-white/80">Hemen yazın</span>
                </a>
                <a
                  href={CONTACT_INFO.map_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-5 bg-white rounded-[20px] border border-[#E7E5E4] hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.12)] hover:-translate-y-0.5 transition-all text-center"
                >
                  <div className="w-10 h-10 rounded-full bg-[#F5F3EF] flex items-center justify-center">
                    <Navigation size={18} className="text-[#1F6E7A]" />
                  </div>
                  <span className="text-[14px] font-medium">Yol Tarifi</span>
                  <span className="text-[14px] text-[#5F6B76]">Google Maps</span>
                </a>
              </div>

              {/* Address & Social Card */}
              <div className="bg-white rounded-[20px] border border-[#E7E5E4] p-6">
                <h3 className="font-heading font-semibold text-[22px] mb-4">Klinik Bilgileri</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin size={16} className="mt-0.5 shrink-0 text-[#D8C7A6]" />
                    <div>
                      <p className="text-[14px] font-medium text-[#1F2937]">Adres</p>
                      <p className="text-[14px] text-[#5F6B76]">{CONTACT_INFO.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock size={16} className="mt-0.5 shrink-0 text-[#D8C7A6]" />
                    <div>
                      <p className="text-[14px] font-medium text-[#1F2937]">Çalışma Saatleri</p>
                      {CONTACT_INFO.schedule ? (
                        formatWorkingHours(CONTACT_INFO.schedule)?.map((g, i) => (
                          <p key={i} className="text-[14px] text-[#5F6B76]">
                            {g.days.length > 1 ? `${g.days[0]} – ${g.days[g.days.length - 1]}` : g.days[0]}
                            {': '}
                            {g.open ? `${g.start} – ${g.end}` : 'Kapalı'}
                          </p>
                        ))
                      ) : (
                        <>
                          <p className="text-[14px] text-[#5F6B76]">{CONTACT_INFO.hours}</p>
                          <p className="text-[14px] text-[#5F6B76]">{CONTACT_INFO.hours_saturday}</p>
                          <p className="text-[14px] text-[#5F6B76]">{CONTACT_INFO.hours_sunday}</p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail size={16} className="mt-0.5 shrink-0 text-[#D8C7A6]" />
                    <div>
                      <p className="text-[14px] font-medium text-[#1F2937]">E-posta</p>
                      <p className="text-[14px] text-[#5F6B76]">{CONTACT_INFO.email}</p>
                    </div>
                  </div>
                </div>

                {/* Social Media inside same card */}
                {(SOCIAL.youtube || SOCIAL.instagram || SOCIAL.facebook) && (
                  <div className="mt-5 pt-5 border-t border-[#E7E5E4]" data-testid="contact-social-media">
                    <h3 className="font-heading font-semibold text-[22px] mb-4">Sosyal Medya</h3>
                    <div className="grid grid-cols-3 gap-3">
                      {SOCIAL.youtube && (
                        <a href={SOCIAL.youtube} target="_blank" rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-[#F5F3EF] hover:bg-[#1F6E7A] hover:text-white text-[#5F6B76] transition-all group"
                          aria-label="YouTube" data-testid="contact-social-youtube">
                          <Youtube size={18} className="group-hover:text-white text-red-500 shrink-0" />
                          <span className="text-sm font-medium">YouTube</span>
                        </a>
                      )}
                      {SOCIAL.instagram && (
                        <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-[#F5F3EF] hover:bg-[#1F6E7A] hover:text-white text-[#5F6B76] transition-all group"
                          aria-label="Instagram" data-testid="contact-social-instagram">
                          <Instagram size={18} className="group-hover:text-white text-pink-500 shrink-0" />
                          <span className="text-sm font-medium">Instagram</span>
                        </a>
                      )}
                      {SOCIAL.facebook && (
                        <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-[#F5F3EF] hover:bg-[#1F6E7A] hover:text-white text-[#5F6B76] transition-all group"
                          aria-label="Facebook" data-testid="contact-social-facebook">
                          <Facebook size={18} className="group-hover:text-white text-blue-600 shrink-0" />
                          <span className="text-sm font-medium">Facebook</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Map */}
              <div className="rounded-[20px] overflow-hidden border border-[#E7E5E4] h-64">
                {CONTACT_INFO.maps_embed ? (
                  <iframe
                    src={CONTACT_INFO.maps_embed}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="eager"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Klinik Konumu"
                  />
                ) : (
                  <a
                    href={CONTACT_INFO.maps_url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full h-full bg-[#F5F3EF] flex flex-col items-center justify-center gap-2 text-[#5F6B76] hover:text-[#1F6E7A] transition-colors"
                  >
                    <MapPin size={24} />
                    <span className="text-sm font-medium">Haritada Görüntüle</span>
                  </a>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
